import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '../services/api';
import { useAuth } from '../contexts/SupabaseAuthContext';
import { toast } from 'sonner';

export function useProjects() {
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuth();

  // Get all projects
  const { data: projects, isLoading, error } = useQuery({
    queryKey: ['projects'],
    queryFn: projectsApi.getProjects,
    enabled: !authLoading && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get single project
  const getProject = (projectId: string) => {
    return useQuery({
      queryKey: ['projects', projectId],
      queryFn: () => projectsApi.getProject(projectId),
      enabled: !authLoading && !!user && !!projectId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  };

  // Create project mutation
  const createProjectMutation = useMutation({
    mutationFn: projectsApi.createProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Project created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create project');
    },
  });

  // Update project mutation
  const updateProjectMutation = useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: any }) => 
      projectsApi.updateProject(projectId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
      toast.success('Project updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update project');
    },
  });

  // Delete project mutation
  const deleteProjectMutation = useMutation({
    mutationFn: projectsApi.deleteProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Project deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete project');
    },
  });

  // Setup onboarding project mutation
  const setupOnboardingProjectMutation = useMutation({
    mutationFn: projectsApi.setupOnboardingProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Project setup completed');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to setup project');
    },
  });

  return {
    projects: projects?.projects || [],
    isLoading,
    error,
    getProject,
    createProject: createProjectMutation.mutateAsync,
    updateProject: updateProjectMutation.mutateAsync,
    deleteProject: deleteProjectMutation.mutateAsync,
    setupOnboardingProject: setupOnboardingProjectMutation.mutateAsync,
    isCreating: createProjectMutation.isPending,
    isUpdating: updateProjectMutation.isPending,
    isDeleting: deleteProjectMutation.isPending,
  };
}

export function useProject(projectId: string) {
  const { user, loading: authLoading } = useAuth();
  
  return useQuery({
    queryKey: ['projects', projectId],
    queryFn: () => projectsApi.getProject(projectId),
    enabled: !authLoading && !!user && !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useProjectConnections(projectId: string) {
  const { user, loading: authLoading } = useAuth();
  
  return useQuery({
    queryKey: ['projects', projectId, 'connections'],
    queryFn: () => projectsApi.getProjectConnections(projectId),
    enabled: !authLoading && !!user && !!projectId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: projectsApi.createProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: any }) =>
      projectsApi.updateProject(projectId, data),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: projectsApi.deleteProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}